<template>
  <v-card flat>
    <v-card-text>
      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="3">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.total_supplier_count }}
            </div>
            <div class="caption">Suppliers</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="green darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_total_revenue) }}
            </div>
            <div class="caption">Total Revenue</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="purple darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_total_inventory_cost) }}
            </div>
            <div class="caption">Total Inventory Cost</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="red darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.grand_total_returned }}
            </div>
            <div class="caption">Total Returns</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Period Info -->
      <v-alert v-if="report" type="info" dense text class="mb-4">
        Supplier performance for period:
        <strong>{{ report.from_date }}</strong> to
        <strong>{{ report.to_date }}</strong>
      </v-alert>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="report.suppliers"
        :headers="headers"
        :search="search"
        item-key="supplier"
        dense
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
        sort-by="total_revenue"
        :sort-desc="true"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search suppliers..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:item.supplier="{ item }">
          <span class="font-weight-bold">{{ item.supplier }}</span>
        </template>
        <template v-slot:item.total_inventory_cost="{ item }">
          {{ formatCurrency(item.total_inventory_cost) }}
        </template>
        <template v-slot:item.total_inventory_retail="{ item }">
          {{ formatCurrency(item.total_inventory_retail) }}
        </template>
        <template v-slot:item.total_revenue="{ item }">
          <span class="green--text font-weight-bold">
            {{ formatCurrency(item.total_revenue) }}
          </span>
        </template>
        <template v-slot:item.gross_profit="{ item }">
          <span :class="item.gross_profit >= 0 ? 'green--text' : 'red--text'">
            {{ formatCurrency(item.gross_profit) }}
          </span>
        </template>
        <template v-slot:item.avg_margin_pct="{ item }">
          <v-chip
            :color="
              item.avg_margin_pct >= 20
                ? 'green'
                : item.avg_margin_pct >= 10
                ? 'orange'
                : item.avg_margin_pct > 0
                ? 'amber darken-1'
                : 'grey'
            "
            dark
            small
          >
            {{ item.avg_margin_pct }}%
          </v-chip>
        </template>
        <template v-slot:item.total_returned="{ item }">
          <v-chip :color="item.total_returned > 0 ? 'red' : 'green'" dark small>
            {{ item.total_returned }}
          </v-chip>
        </template>
        <template v-slot:item.return_rate_pct="{ item }">
          <v-chip
            :color="
              item.return_rate_pct >= 10
                ? 'red'
                : item.return_rate_pct >= 5
                ? 'orange'
                : 'green'
            "
            dark
            small
          >
            {{ item.return_rate_pct }}%
          </v-chip>
        </template>
        <template v-slot:item.out_of_stock_count="{ item }">
          <v-chip
            :color="item.out_of_stock_count > 0 ? 'red' : 'green'"
            dark
            small
          >
            {{ item.out_of_stock_count }}
          </v-chip>
        </template>
        <template v-slot:item.low_stock_count="{ item }">
          <v-chip
            :color="item.low_stock_count > 0 ? 'orange' : 'green'"
            dark
            small
          >
            {{ item.low_stock_count }}
          </v-chip>
        </template>
        <template v-slot:item.item_types="{ item }">
          <v-chip
            v-for="type in item.item_types.slice(0, 3)"
            :key="type"
            x-small
            class="mr-1"
            outlined
          >
            {{ type }}
          </v-chip>
          <span v-if="item.item_types.length > 3" class="caption grey--text">
            +{{ item.item_types.length - 3 }} more
          </span>
        </template>

        <!-- Expanded Row for details -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-4 grey lighten-5">
            <strong>Item Types:</strong>
            {{ item.item_types.join(", ") || "N/A" }}
          </td>
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1">mdi-truck-check</v-icon>
        <p class="mt-4 subtitle-1">
          Click "Generate" to analyze supplier performance.
        </p>
        <p class="caption grey--text">
          Compares suppliers by revenue, margins, return rates, and stock
          health.
        </p>
      </div>

      <!-- No Results -->
      <div
        v-if="report && report.suppliers && report.suppliers.length === 0"
        class="text-center pa-10"
      >
        <v-icon size="64" color="grey">mdi-truck-remove</v-icon>
        <p class="mt-4 subtitle-1 grey--text">
          No supplier data found for the selected filters.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "SupplierPerformanceReport",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      headers: [
        { text: "Supplier", value: "supplier" },
        { text: "Items", value: "total_items", align: "center" },
        {
          text: "Stock on Hand",
          value: "total_stock_on_hand",
          align: "center",
        },
        { text: "Inv. Cost", value: "total_inventory_cost", align: "end" },
        { text: "Qty Sold", value: "total_qty_sold", align: "center" },
        { text: "Revenue", value: "total_revenue", align: "end" },
        { text: "Gross Profit", value: "gross_profit", align: "end" },
        { text: "Margin %", value: "avg_margin_pct", align: "center" },
        { text: "Returns", value: "total_returned", align: "center" },
        { text: "Return Rate", value: "return_rate_pct", align: "center" },
        { text: "Out of Stock", value: "out_of_stock_count", align: "center" },
        { text: "Low Stock", value: "low_stock_count", align: "center" },
        { text: "Categories", value: "item_types", sortable: false },
      ],
    };
  },
  methods: {
    ...mapActions({
      fetchReport: "inventory_reports/get_supplier_performance_report",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async generate() {
      try {
        let params = {};
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;
        if (this.filters.item_type_id)
          params.item_type_id = this.filters.item_type_id;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        this.report = await this.fetchReport(params);
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating supplier performance report: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>
